<template>
  <template v-if="loading">
    <template>
      <Skeleton class="h-[64px] mb-5" />
      <Skeleton class="h-[48px] mb-5" />
    </template>

    <div
      v-for="n in 3"
      :key="n"
      class="w-full h-[64px] flex items-center first:border-t border-b border-gray-800/8"
    >
      <Skeleton class="w-6 h-6 rounded-full mr-2.5" />
      <div>
        <Skeleton class="w-[200px] h-[14px] mb-1.5" />
        <Skeleton class="w-[140px] h-[12px]" />
      </div>
    </div>
  </template>

  <template v-else>
    <UpgradeToProCallout v-if="showProCallout" class="mb-[14px]">
      Ownership options are only available with a Pro Membership
    </UpgradeToProCallout>

    <div :class="inviteClasses" class="flex flex-col gap-y-[14px] mb-[14px]">
      <FileInfo :file="props.file"> Owner(s) of the original work </FileInfo>

      <template v-if="isOwner">
        <InviteInput
          v-model="usersToInvite"
          :disabled="!canInvite"
          :show-own-entity="true"
        />

        <Button
          v-if="!hideSendButton"
          variant="primary"
          class="ml-auto"
          :disabled="!canSend"
          :loading="sendInvitesLoading"
          @click="handleInviteClick"
        >
          Send invite
        </Button>
      </template>
    </div>

    <Table :class="inviteClasses">
      <Table.Row :hover="false" class="cursor-default!">
        <template #media>
          <Avatar
            :size="24"
            :src="props.file.owner.avatar.url"
            class="mt-[14px] self-start"
          />
        </template>

        <Table.Cell primary class="items-start! py-[12px]!">
          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-x-2">
              <div class="min-w-0">
                <div class="truncate">{{ props.file.owner.name }}</div>
              </div>
              <div
                class="flex shrink-0 items-center"
                :class="isSoleOwner ? '-mr-[10px]' : 'mr-[29px]'"
              >
                <Pill variant="success">Owner</Pill>
              </div>
            </div>
            <div class="mt-[6px] text-[13px] leading-[18px]">
              <span class="font-medium">Principal copyright owner. </span>
              <span class="text-gray-500">
                You must sell or reassign ownership of the registration to
                change this owner.
              </span>
            </div>
          </div>
        </Table.Cell>
      </Table.Row>

      <Table.Row
        v-for="item in coOwnerItems"
        :key="getKey(item)"
        :hover="false"
        class="cursor-default!"
      >
        <template #media>
          <Avatar
            v-if="isEntity(item)"
            :size="24"
            :src="getEntityAvatar(item)"
          />
          <div
            v-else
            class="size-6 flex items-center justify-center opacity-50"
          >
            <SendIcon class="text-gray-800" />
          </div>
        </template>

        <Table.Cell primary class="min-h-[64px]!">
          <div>
            <div :class="getNameClass(item)">
              {{ getEntityNameOrEmail(item) }}
            </div>
            <div class="text-[13px] leading-[18px] text-gray-800/50">
              <template v-if="isUser(item)">
                {{ (getEntityData(item) as UserType).username }}
              </template>
              <template v-if="isOrganization(item)">
                Managed by {{ getOrgOwnerName(item) }}
              </template>
            </div>
          </div>
        </Table.Cell>

        <Table.Cell align="right" class="min-h-[64px]! pr-[7px]">
          <div
            v-if="isInvited(item)"
            class="text-[13px] leading-[20px] opacity-50"
          >
            Invited
          </div>
          <div v-else class="flex items-center gap-x-1.5">
            <Pill v-for="pill in getPills(item)" :key="pill">
              {{ pill }}
            </Pill>
          </div>
          <RowActionsMenu
            v-if="showThreeDots(item)"
            class="ml-[14px]"
            @remove="handleRemoveClick(item)"
          />
        </Table.Cell>
      </Table.Row>
    </Table>
  </template>
</template>

<script setup lang="ts">
import { openModal, Modals } from '@/shared/dialogs/modals'
import { triggerDialogEvent } from '@/shared/dialogs/dialog'
import { graphql } from '@/shared/graphql/router'
import {
  FileRole,
  InviteType,
  type File,
  type User,
  type Organization,
  type CoOwnersConnection,
} from '@/shared/graphql/router/graphql'
import {
  UserPermission,
  type Entity as LegacyEntity,
  type BasicUser as LegacyBasicUser,
  type Organization as LegacyOrganization,
} from '@/shared/graphql/legacy/generated'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { Avatar, Pill, Button, Skeleton, Table } from '@dacr/library'
import { SendIcon } from '@dacr/icons'
import RowActionsMenu from '../RowActionsMenu.vue'
import { useUserStore } from '@/shared/stores/user'
import { computed, ref } from 'vue'
import FileInfo from '@/features/explorer/components/FileInfo.vue'
import InviteInput from '@/features/search/components/InviteInput.vue'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import UpgradeToProCallout from '@/features/billing/components/UpgradeToProCallout.vue'

type UserType = User
type OrgType = Organization

const FILE_OWNERS_QUERY = graphql(`
  query FileOwners($id: ID!) {
    file(id: $id) {
      id
      coOwners(input: { first: 100, filterBy: ALL }) {
        totalCount
        edges {
          node {
            ... on EntityWithRole {
              role
              entity {
                ... on User {
                  id
                  name
                  username
                  avatar {
                    url
                  }
                }
                ... on Organization {
                  id
                  name
                  avatar {
                    url
                  }
                  owner {
                    ... on User {
                      id
                      name
                    }
                  }
                }
              }
            }
            ... on EmailInvite {
              email
            }
          }
        }
      }
    }
  }
`)

const ADD_CO_OWNERS_MUTATION = graphql(`
  mutation AddCoOwners($input: AddCoOwnersInput!) {
    addCoOwners(input: $input) {
      error {
        code
        message
      }
      file {
        id
        coOwners(input: { first: 100, filterBy: ALL }) {
          totalCount
          edges {
            node {
              ... on EntityWithRole {
                role
                entity {
                  ... on User {
                    id
                    name
                    username
                    avatar {
                      url
                    }
                  }
                  ... on Organization {
                    id
                    name
                    avatar {
                      url
                    }
                    owner {
                      ... on User {
                        id
                        name
                      }
                    }
                  }
                }
              }
              ... on EmailInvite {
                email
              }
            }
          }
        }
      }
    }
  }
`)

const props = defineProps<{
  file: File
  hideSendButton?: boolean
}>()

const { result, loading } = useQuery(
  FILE_OWNERS_QUERY,
  { id: props.file.copyright.id || '' },
  {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    clientId: 'router',
  }
)

const { mutate: sendInvites, loading: sendInvitesLoading } = useMutation(
  ADD_CO_OWNERS_MUTATION,
  { clientId: 'router' }
)

const toastStore = useToastStore()
const userStore = useUserStore()

const usersToInvite = ref<LegacyEntity[]>([])

const isBasicUser = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const isOwner = computed(() => props.file.role === FileRole.Owner)

const canInvite = computed(() => isOwner.value && !isBasicUser.value)

const canSend = computed(
  () => usersToInvite.value.length > 0 && canInvite.value
)

const showProCallout = computed(() => isBasicUser.value && isOwner.value)

type CoOwnerNode = NonNullable<
  NonNullable<typeof result.value>['file']
>['coOwners']['edges'][number]['node']

const items = computed(
  () => result.value?.file?.coOwners.edges.map((e) => e.node) ?? []
)

const inviteClasses = computed(() => ({
  'opacity-30 pointer-events-none': isOwner.value && isBasicUser.value,
}))

const getNameClass = (item: CoOwnerNode) => ({
  'text-[13px] opacity-50': item.__typename === 'EmailInvite',
})

type CoOwnerEmailInvite = Extract<CoOwnerNode, { email: string }>
type CoOwnerEntityNode = Exclude<CoOwnerNode, CoOwnerEmailInvite>

const isEmailInvite = (item: CoOwnerNode): item is CoOwnerEmailInvite =>
  'email' in item

const asEntity = (item: CoOwnerNode) => item as CoOwnerEntityNode

const coOwnerItems = computed(() =>
  items.value.filter(
    (item) =>
      isEmailInvite(item) || asEntity(item).entity.id !== props.file.owner.id
  )
)

const isSoleOwner = computed(() => coOwnerItems.value.length === 0)

const getKey = (item: CoOwnerNode) =>
  isEmailInvite(item) ? item.email : asEntity(item).entity.id

const isEntity = (item: CoOwnerNode) => !isEmailInvite(item)

const getEntityData = (item: CoOwnerNode) =>
  isEmailInvite(item) ? null : asEntity(item).entity

const getEntityAvatar = (item: CoOwnerNode) =>
  isEmailInvite(item) ? undefined : asEntity(item).entity.avatar.url

const getEntityNameOrEmail = (item: CoOwnerNode) =>
  isEmailInvite(item) ? item.email : asEntity(item).entity.name

const getInviteType = (user: LegacyEntity) => {
  if (user.id === user.name) return InviteType.Email
  return user.__typename === 'BasicUser' ? InviteType.User : InviteType.Org
}

const showThreeDots = (item: CoOwnerNode) => {
  if (isEmailInvite(item)) return isOwner.value
  const e = asEntity(item)
  if (e.role === FileRole.Owner) return false
  if (isOwner.value) return true
  if (e.entity.__typename === 'Organization') {
    const orgOwner = (e.entity as OrgType).owner
    return orgOwner?.__typename === 'User' && orgOwner.id === userStore.user?.id
  }
  return e.entity.id === userStore.user?.id
}

const isInvited = (item: CoOwnerNode) =>
  isEmailInvite(item) || asEntity(item).role === FileRole.Invited

const getPills = (item: CoOwnerNode) => {
  if (isEmailInvite(item)) return []
  const e = asEntity(item)
  if (e.role === FileRole.Owner) return ['Admin']
  if (e.role === FileRole.CoOwner) return ['Owner']
  return []
}

const isUser = (item: CoOwnerNode) =>
  !isEmailInvite(item) && asEntity(item).entity.__typename === 'User'

const isOrganization = (item: CoOwnerNode) =>
  !isEmailInvite(item) && asEntity(item).entity.__typename === 'Organization'

const getOrgOwnerName = (item: CoOwnerNode) => {
  if (isEmailInvite(item)) return ''
  const org = asEntity(item).entity as OrgType
  return org.owner?.__typename === 'User' ? org.owner.name : ''
}

const handleRemoveClick = (item: CoOwnerNode) => {
  let inviteType: InviteType
  let invitePayload: { id?: string; email?: string }

  if (isEmailInvite(item)) {
    inviteType = InviteType.Email
    invitePayload = { email: item.email }
  } else {
    const e = asEntity(item)
    inviteType =
      e.entity.__typename === 'User' ? InviteType.User : InviteType.Org
    invitePayload = { id: e.entity.id }
  }

  openModal(Modals.RemoveFileOwner, {
    stack: true,
    props: {
      fileId: props.file.id,
      invite: {
        ...invitePayload,
        type: inviteType,
      },
      name: getEntityNameOrEmail(item),
    },
    events: {
      success() {
        triggerDialogEvent('success', {
          ...props.file,
        })
      },
    },
  })
}

const buildOptimisticEdge = (user: LegacyEntity) => {
  if (getInviteType(user) === InviteType.Email) {
    return {
      __typename: 'CoOwnerEdge' as const,
      cursor: '',
      node: { __typename: 'EmailInvite' as const, email: user.id },
    }
  }

  const isUser = user.__typename === 'BasicUser'
  return {
    __typename: 'CoOwnerEdge' as const,
    cursor: '',
    node: {
      __typename: 'EntityWithRole' as const,
      role: FileRole.Invited,
      entity: isUser
        ? {
            __typename: 'User' as const,
            id: user.id,
            name: user.name,
            username: (user as LegacyBasicUser).username,
            avatar: {
              __typename: 'Image' as const,
              url: (user as LegacyBasicUser).avatarUrl,
            },
          }
        : {
            __typename: 'Organization' as const,
            id: user.id,
            name: user.name,
            avatar: {
              __typename: 'Image' as const,
              url: (user as LegacyOrganization).avatarUrl,
            },
            owner: (user as LegacyOrganization).owner
              ? {
                  __typename: 'User' as const,
                  id: (user as LegacyOrganization).owner!.id,
                  name: (user as LegacyOrganization).owner!.name,
                }
              : { __typename: 'Organization' as const },
          },
    },
  }
}

const handleInviteClick = async () => {
  const invitedUsers = [...usersToInvite.value]

  const invites = invitedUsers.map((user) => ({
    ...(getInviteType(user) === InviteType.Email
      ? { email: user.id }
      : { id: user.id }),
    type: getInviteType(user),
  }))

  const currentEdges = result.value?.file?.coOwners.edges ?? []
  const currentCount = result.value?.file?.coOwners.totalCount ?? 0

  try {
    await sendInvites(
      { input: { fileId: props.file.id, invites } },
      {
        update(cache) {
          cache.modify({
            id: cache.identify({ __typename: 'File', id: props.file.id }),
            fields: {
              coOwners(existing, { storeFieldName }) {
                if (!storeFieldName.includes('ALL')) return existing
                const data = existing as CoOwnersConnection
                if (!data?.edges) return existing

                return {
                  ...existing,
                  totalCount: currentCount + invitedUsers.length,
                  edges: [
                    ...currentEdges,
                    ...invitedUsers.map(buildOptimisticEdge),
                  ],
                }
              },
            },
          })
        },
      }
    )

    usersToInvite.value = []

    toastStore.showToast({
      type: ToastType.Success,
      content: 'Invites were successfully sent',
    })
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not send invites - please try again later',
      error: error as Error,
    })
  }
}

defineExpose({ handleInviteClick, canSend, sendInvitesLoading })
</script>
